import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';

interface Role {
  id: number;
  name: string;
  description?: string;
  permissions: string[];
  isActive: boolean;
  _count?: { subUsers: number };
}
interface PermissionItem { key: string; label: string; }

export function MerchantRolesTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Role | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: roles, isLoading } = useQuery<Role[]>({
    queryKey: ['merchant', 'roles'],
    queryFn: async () => (await api.get<Role[]>('/merchant/roles')).data,
  });
  const { data: catalog } = useQuery<PermissionItem[]>({
    queryKey: ['merchant', 'permissions-catalog'],
    queryFn: async () => (await api.get<PermissionItem[]>('/merchant/permissions/catalog')).data,
    staleTime: Infinity,
  });

  const save = useMutation({
    mutationFn: async (r: Partial<Role> & { id?: number }) => {
      if (r.id) return (await api.patch(`/merchant/roles/${r.id}`, r)).data;
      return (await api.post('/merchant/roles', r)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant', 'roles'] });
      toast.success('Role saved');
      setShowForm(false);
      setEditing(null);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/merchant/roles/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant', 'roles'] }); toast.success('Role deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Delete failed'),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Roles & Permissions</h3>
          <p className="text-sm text-slate-500">Group permissions into roles, then assign roles to your team.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-primary text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          <Plus size={16} /> New role
        </button>
      </div>

      {isLoading && <div className="p-8 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>}

      <div className="space-y-2">
        {roles?.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3">
            <div className="bg-primary/10 text-primary p-2 rounded"><ShieldCheck size={18} /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-medium text-slate-900">{r.name}</div>
                <span className="text-xs text-slate-500">{r._count?.subUsers ?? 0} member{(r._count?.subUsers ?? 0) === 1 ? '' : 's'}</span>
                {!r.isActive && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Inactive</span>}
              </div>
              {r.description && <div className="text-xs text-slate-500">{r.description}</div>}
              <div className="flex flex-wrap gap-1 mt-1.5">
                {r.permissions.length === 0 && <span className="text-xs text-slate-400">No permissions</span>}
                {r.permissions.slice(0, 8).map((p) => (
                  <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{p}</span>
                ))}
                {r.permissions.length > 8 && <span className="text-[10px] text-slate-400">+{r.permissions.length - 8} more</span>}
              </div>
            </div>
            <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"><Pencil size={14} /></button>
            <button onClick={() => { if (confirm(`Delete role "${r.name}"?`)) del.mutate(r.id); }} className="p-1.5 text-danger hover:bg-danger/10 rounded"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {showForm && (
        <RoleForm
          initial={editing}
          catalog={catalog ?? []}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={(data) => save.mutate({ ...data, ...(editing ? { id: editing.id } : {}) })}
          busy={save.isPending}
        />
      )}
    </div>
  );
}

function RoleForm({ initial, catalog, onSave, onCancel, busy }: {
  initial: Role | null; catalog: PermissionItem[];
  onSave: (data: Partial<Role>) => void; onCancel: () => void; busy: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [permissions, setPermissions] = useState<string[]>(initial?.permissions ?? []);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const togglePerm = (k: string) => setPermissions((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl p-5 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-3">{initial ? 'Edit role' : 'New role'}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Role name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Accounts"
              className="w-full px-3 py-2 border rounded-lg outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this role can do"
              className="w-full px-3 py-2 border rounded-lg outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Permissions ({permissions.length} selected)</label>
            <div className="grid grid-cols-2 gap-1 mt-1 border rounded-lg p-3 max-h-72 overflow-y-auto">
              {catalog.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={permissions.includes(p.key)} onChange={() => togglePerm(p.key)} />
                  <span className="truncate">{p.label}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={() => onSave({ name, description, permissions, isActive })} disabled={busy || !name}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg disabled:opacity-50">
            {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
